const SHORT_HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{3}$/;
const FULL_HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;
const MINIMUM_WHITE_TEXT_CONTRAST_RATIO = 4.5;
const CONTRAST_DARKEN_STEP_RATIO = 0.9;
const MAXIMUM_CONTRAST_DARKEN_ITERATIONS = 50;
const HOVER_LIGHTEN_RATIO = 0.18;
const SHADCN_PRIMARY_FOREGROUND_HSL = '0 0% 100%';

export interface RgbColor {
  red: number;
  green: number;
  blue: number;
}

export interface WorkspaceBrandThemeVariables {
  zooPrimaryRgb: string;
  zooPrimaryHoverRgb: string;
  shadcnPrimaryHsl: string;
  shadcnPrimaryForegroundHsl: string;
}

export function parseHexColor(hexColor: string): RgbColor | null {
  const trimmedHexColor = hexColor.trim();
  if (SHORT_HEX_COLOR_PATTERN.test(trimmedHexColor)) {
    const redDigit = trimmedHexColor.charAt(1);
    const greenDigit = trimmedHexColor.charAt(2);
    const blueDigit = trimmedHexColor.charAt(3);
    return {
      red: parseInt(redDigit + redDigit, 16),
      green: parseInt(greenDigit + greenDigit, 16),
      blue: parseInt(blueDigit + blueDigit, 16),
    };
  }
  if (FULL_HEX_COLOR_PATTERN.test(trimmedHexColor)) {
    return {
      red: parseInt(trimmedHexColor.substring(1, 3), 16),
      green: parseInt(trimmedHexColor.substring(3, 5), 16),
      blue: parseInt(trimmedHexColor.substring(5, 7), 16),
    };
  }
  return null;
}

function convertColorChannelToLinear(channelValue: number): number {
  const normalizedChannel = channelValue / 255;
  if (normalizedChannel <= 0.03928) {
    return normalizedChannel / 12.92;
  }
  return Math.pow((normalizedChannel + 0.055) / 1.055, 2.4);
}

function computeRelativeLuminance(rgbColor: RgbColor): number {
  const linearRed = convertColorChannelToLinear(rgbColor.red);
  const linearGreen = convertColorChannelToLinear(rgbColor.green);
  const linearBlue = convertColorChannelToLinear(rgbColor.blue);
  return 0.2126 * linearRed + 0.7152 * linearGreen + 0.0722 * linearBlue;
}

export function computeWhiteTextContrastRatio(rgbColor: RgbColor): number {
  const backgroundLuminance = computeRelativeLuminance(rgbColor);
  return 1.05 / (backgroundLuminance + 0.05);
}

function darkenRgbColor(rgbColor: RgbColor, ratio: number): RgbColor {
  return {
    red: Math.round(rgbColor.red * ratio),
    green: Math.round(rgbColor.green * ratio),
    blue: Math.round(rgbColor.blue * ratio),
  };
}

export function ensureAccessibleContrastWithWhiteText(rgbColor: RgbColor): RgbColor {
  let candidateColor = rgbColor;
  let iterationCount = 0;
  while (
    computeWhiteTextContrastRatio(candidateColor) < MINIMUM_WHITE_TEXT_CONTRAST_RATIO &&
    iterationCount < MAXIMUM_CONTRAST_DARKEN_ITERATIONS
  ) {
    candidateColor = darkenRgbColor(candidateColor, CONTRAST_DARKEN_STEP_RATIO);
    iterationCount += 1;
  }
  return candidateColor;
}

function lightenColorChannel(channelValue: number, ratio: number): number {
  return Math.round(channelValue + (255 - channelValue) * ratio);
}

export function deriveHoverColor(rgbColor: RgbColor): RgbColor {
  const lightenedColor: RgbColor = {
    red: lightenColorChannel(rgbColor.red, HOVER_LIGHTEN_RATIO),
    green: lightenColorChannel(rgbColor.green, HOVER_LIGHTEN_RATIO),
    blue: lightenColorChannel(rgbColor.blue, HOVER_LIGHTEN_RATIO),
  };
  if (computeWhiteTextContrastRatio(lightenedColor) >= MINIMUM_WHITE_TEXT_CONTRAST_RATIO) {
    return lightenedColor;
  }
  return darkenRgbColor(rgbColor, 1 - HOVER_LIGHTEN_RATIO);
}

function formatRgbTriplet(rgbColor: RgbColor): string {
  return rgbColor.red + ' ' + rgbColor.green + ' ' + rgbColor.blue;
}

function formatHslTriplet(rgbColor: RgbColor): string {
  const normalizedRed = rgbColor.red / 255;
  const normalizedGreen = rgbColor.green / 255;
  const normalizedBlue = rgbColor.blue / 255;
  const maximumChannel = Math.max(normalizedRed, normalizedGreen, normalizedBlue);
  const minimumChannel = Math.min(normalizedRed, normalizedGreen, normalizedBlue);
  const lightness = (maximumChannel + minimumChannel) / 2;
  const chroma = maximumChannel - minimumChannel;

  let hueInDegrees = 0;
  let saturation = 0;
  if (chroma !== 0) {
    saturation = chroma / (1 - Math.abs(2 * lightness - 1));
    if (maximumChannel === normalizedRed) {
      hueInDegrees = ((normalizedGreen - normalizedBlue) / chroma) % 6;
    } else if (maximumChannel === normalizedGreen) {
      hueInDegrees = (normalizedBlue - normalizedRed) / chroma + 2;
    } else {
      hueInDegrees = (normalizedRed - normalizedGreen) / chroma + 4;
    }
    hueInDegrees = hueInDegrees * 60;
    if (hueInDegrees < 0) {
      hueInDegrees += 360;
    }
  }

  const roundedHue = Math.round(hueInDegrees);
  const roundedSaturationPercent = Math.round(saturation * 100);
  const roundedLightnessPercent = Math.round(lightness * 100);
  return roundedHue + ' ' + roundedSaturationPercent + '% ' + roundedLightnessPercent + '%';
}

export function buildWorkspaceBrandThemeVariables(
  hexColor: string | null
): WorkspaceBrandThemeVariables | null {
  if (hexColor === null) {
    return null;
  }
  const parsedColor = parseHexColor(hexColor);
  if (parsedColor === null) {
    return null;
  }
  const accessibleColor = ensureAccessibleContrastWithWhiteText(parsedColor);
  const hoverColor = deriveHoverColor(accessibleColor);
  return {
    zooPrimaryRgb: formatRgbTriplet(accessibleColor),
    zooPrimaryHoverRgb: formatRgbTriplet(hoverColor),
    shadcnPrimaryHsl: formatHslTriplet(accessibleColor),
    shadcnPrimaryForegroundHsl: SHADCN_PRIMARY_FOREGROUND_HSL,
  };
}
